import React, { useCallback } from 'react';

import Tooltip from 'common/components/Tooltip';
import Quantity from 'common/components/Quantity';

import {
  FieldPath, Path, PathValue, UseFormWatch,
} from 'react-hook-form';
// eslint-disable-next-line import/no-unresolved
import { UseFormSetValue } from 'react-hook-form/dist/types/form';
import classNames from 'classnames';
import styles from './styles.module.scss';
import { FallbackFormValues } from '../../../../../utils/type-tools';
import { ChangeHandlerType, GeneratorChangeHandlerType } from '../../../../../common/components/FormHF/types';
import { GeneratorFieldType } from '../../../../../common/components/FieldGenerator/types';
import { useDebounceCallback } from '../../../../../hooks/useDebouncedCallback';
import { FieldsType } from '../../../../../common/types/fields';

export const MIN_QUANTITY = 1;
export const MAX_QUANTITY = 999;

interface QuantityProps<TFormValue extends FallbackFormValues> {
    name: FieldPath<TFormValue>,
    watch: UseFormWatch<Record<keyof TFormValue, any>>,
    setValue: UseFormSetValue<TFormValue>,
    onChangeHandler?: GeneratorChangeHandlerType<TFormValue>,
    debounceDelay?: number,
    field: GeneratorFieldType,
    className?: string,
}

export const QuantityConnected = <TFormValue extends FallbackFormValues>({
  className,
  watch,
  name,
  setValue,
  onChangeHandler,
  debounceDelay,
  field,
}: QuantityProps<TFormValue>) => {
  const quantityField = watch(name) as number || MIN_QUANTITY;
  const configQuantity = +quantityField;

  const onChangeHandlerDebounced = useDebounceCallback(onChangeHandler, debounceDelay || 500);

  const onChangeHandlerWrapper = useCallback<ChangeHandlerType<TFormValue>>((fieldVal) => {
    if (onChangeHandler) {
      switch (field.type) {
        case FieldsType.textarea:
        case FieldsType.input: {
          onChangeHandlerDebounced(field.type, name, fieldVal);
          break;
        }
        default: {
          onChangeHandler(field.type, name, fieldVal);
        }
      }
    }
  }, [field.type, name, onChangeHandler, onChangeHandlerDebounced]);

  const handleSetCounter = useCallback((count: number) => {
    setValue(name as Path<TFormValue>, count as PathValue<TFormValue, Path<TFormValue>>);
    onChangeHandlerWrapper(Math.max(Math.min(count, MAX_QUANTITY), MIN_QUANTITY) as PathValue<TFormValue, Path<TFormValue>>);
  }, [onChangeHandlerWrapper]);

  return (
    <>
      <div className={classNames(styles.quantityLabel, 'common-components-Input__label')}>
        Assembly Qty:
        <Tooltip
          className={styles.iconTooltip}
          content="Please enter the required quantity for this assembly."
        />
      </div>
      <Quantity
        className={className}
        count={configQuantity}
        handleSetCounter={handleSetCounter}
      />
    </>
  );
};
